// Async id -> name lookups for the analytics Campaign category (and the
// request-side provider field). Campaign/segment/creative/provider are small,
// fully-listable sets (unlike the 136k-row city dict), so we fetch the whole
// list once per query and filter it client-side. Shape mirrors DictEntry
// ({ id, name }) so the AsyncLookupMultiSelectField renders them uniformly —
// here `id` is a uuid string.

const API_BASE = import.meta.env?.VITE_API_URL ?? "/api";
const API_TOKEN = import.meta.env?.VITE_API_TOKEN;

const buildApiUrl = (path: string): string => {
  const base = API_BASE.endsWith("/") ? API_BASE.slice(0, -1) : API_BASE;

  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

const getAuthToken = (): string | null => {
  if (typeof window === "undefined") {
    return API_TOKEN ?? null;
  }

  return window.localStorage.getItem("auth_token") ?? API_TOKEN ?? null;
};

// `icon` is only populated for campaigns (a lucide IconName from the campaign
// row) so the results table can render the campaign glyph next to its name.
export type LookupEntry = { id: string; name: string; icon?: string | null };

// The lookups the Campaign category (and the provider field) draw from. The
// value drives which endpoint AsyncLookupMultiSelectField queries.
export type LookupKind = "campaigns" | "segments" | "creatives" | "providers";

const LOOKUP_PATH: Record<LookupKind, string> = {
  campaigns: "/campaigns",
  segments: "/segments",
  creatives: "/creatives",
  providers: "/providers",
};

type ListItem = { uuid: string; name: string; icon?: string | null };

const fetchList = async (
  path: string,
  signal?: AbortSignal,
): Promise<ListItem[]> => {
  const headers: HeadersInit = {};
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const requestInit: RequestInit = {
    method: "GET",
    headers,
    credentials: "include",
  };
  if (signal) {
    requestInit.signal = signal;
  }

  const response = await fetch(buildApiUrl(path), requestInit);
  if (!response.ok) {
    const message = await response.text();
    throw new Error(
      message || `Request failed with status ${response.status.toString()}`,
    );
  }

  // /campaigns, /segments, /creatives return a paginated ENVELOPE
  // ({ data, pagination }); /providers returns a bare array. Normalize both so
  // the downstream .filter never runs against a non-array (that silently emptied
  // the Campaign dropdown AND the table name resolution).
  const json: unknown = await response.json();
  const arr = Array.isArray(json)
    ? json
    : ((json as { data?: unknown; items?: unknown; results?: unknown }).data ??
      (json as { items?: unknown }).items ??
      (json as { results?: unknown }).results ??
      []);

  return arr as ListItem[];
};

// Fetch a lookup list and filter it by a case-insensitive substring match on the
// name (empty query returns all). Returns {id, name, icon?} with id = uuid;
// `icon` is carried through for campaigns.
export const searchLookup = async (
  kind: LookupKind,
  q: string,
  signal?: AbortSignal,
): Promise<LookupEntry[]> => {
  const items = await fetchList(LOOKUP_PATH[kind], signal);
  const needle = q.trim().toLowerCase();
  const matched = needle
    ? items.filter((item) => item.name.toLowerCase().includes(needle))
    : items;

  return matched.map((item) => ({
    id: item.uuid,
    name: item.name,
    icon: item.icon ?? null,
  }));
};
